import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router";

import type { TicketCommentRequest } from "@/api/types";
import { ErrorPage } from "@/components/Error/ErrorPage";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import type { ResidentCreateCommentPayload } from "@/components/Ticket/TicketResidentCommentField";
import { FullSizeLoader } from "@/design-system/FullSizeLoader";
import { isHttpError } from "@/helpers/Network/errors";
import { useUploadImage } from "@/hooks/Network/useUploadImage";
import { useUploadVideo } from "@/hooks/Network/useUploadVideo";
import { useSlug } from "@/hooks/useSlug";
import { ticketMutations } from "@/queries/tickets/mutations";
import { useTicketQueries } from "@/queries/tickets/queryOptions";
import { routes } from "@/routes";
import { useTranslation } from "@/translations";

import { ResidentTicketDetailsContent, type ResidentTicketDetailsContentProps } from "./ResidentTicketDetailsContent";

export function ResidentTicketDetailsPage(): React.ReactNode {
  const slug = useSlug();
  const { id: ticketId } = useParams<{ id: string }>();
  const ticketIdForQuery = ticketId ?? "";
  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const navigate = useNavigate();

  const ticketQueries = useTicketQueries();
  const {
    data: ticket,
    isPending: isLoadingTicket,
    error: ticketDetailsError,
  } = useQuery(ticketQueries.detailsResident(ticketIdForQuery));

  const commentsQuery = useInfiniteQuery(ticketQueries.commentsResident(ticketIdForQuery));

  const { uploadFormImage } = useUploadImage();
  const { uploadFormVideo } = useUploadVideo({});

  const reply = ticketMutations.useResidentReply(ticketIdForQuery);
  const markAsReadMutation = ticketMutations.useMarkTicketAsRead(ticketIdForQuery);

  const comments = useMemo(() => commentsQuery.data?.pages.flatMap((x) => x.items) || [], [commentsQuery.data?.pages]);

  async function createComment({ content, images, videos, internal }: ResidentCreateCommentPayload) {
    const payload: TicketCommentRequest = {
      content,
      accessType: internal ? "internal" : "public",
    };

    if (images && images.length > 0) {
      const uploadedImage = await uploadFormImage(images[0]);

      payload.imageId = uploadedImage?.id;
    }

    const uploadedVideoIds: string[] = [];
    if (videos && videos.length > 0) {
      for (const video of videos) {
        const uploadedVideo = await uploadFormVideo(video);

        if (uploadedVideo) {
          uploadedVideoIds.push(uploadedVideo.id);
        }
      }

      payload.videoIds = uploadedVideoIds;
    }

    await reply.mutateAsync(payload);
  }

  useEffect(() => {
    if (isHttpError(ticketDetailsError) && ticketDetailsError.status === 404) {
      void navigate(routes.tickets.list({ slug }));
      showFlashToast({
        variant: "alert",
        message: t("page.tickets.details.errors.not-found"),
      });
    }
  }, [navigate, showFlashToast, t, ticketDetailsError, slug]);

  // Mark as read called everytime ticket is loaded
  const markAsRead = markAsReadMutation.mutate;
  useEffect(() => {
    const timeout = setTimeout(() => {
      if (ticket?.id && ticket.unread) {
        markAsRead();
      }
    }, 500);

    return () => clearTimeout(timeout);
  }, [markAsRead, ticket?.id, ticket?.unread]);

  const error = ticketDetailsError || commentsQuery.error;
  if (error) {
    return <ErrorPage error={error} />;
  }

  if (isLoadingTicket) {
    return <FullSizeLoader withPadding />;
  }

  const contentProps: ResidentTicketDetailsContentProps = {
    ticket,
    comments,
    isFetchingMoreComments: commentsQuery.isFetchingNextPage || commentsQuery.isPending,
    hasMoreComments: commentsQuery.hasNextPage || false,
    fetchMoreComments: commentsQuery.fetchNextPage,
    onCreateComment: createComment,
  };

  return <ResidentTicketDetailsContent {...contentProps} />;
}
